import { useState } from 'react';
import DonationWidget from '@/components/donation/DonationWidget';
import { Button, LoadingSpinner, Alert, Card } from '@/components/ui';
import { useGivingOptions } from '@/hooks';
import type { RecurringSchedule } from '@/types/donation';

function DonationPage() {
  const [selectedOption, setSelectedOption] = useState<{
    schedule: RecurringSchedule;
    amount: number;
  } | null>(null);

  // Fetch giving options from API
  const { data: givingOptionsData, isLoading, error } = useGivingOptions();

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12 min-h-screen">
        <Card className="max-w-2xl mx-auto">
          <LoadingSpinner message="Loading giving options..." />
        </Card>
      </div>
    );
  }

  // Error state
  if (error || !givingOptionsData || !givingOptionsData.data) {
    return (
      <div className="container mx-auto px-4 py-12 min-h-screen">
        <Card className="max-w-2xl mx-auto">
          <Alert variant="error" title="Unable to Load Giving Options">
            {error?.message || 'Failed to load giving options. Please try again later.'}
          </Alert>
        </Card>
      </div>
    );
  }

  const givingOptions = givingOptionsData.data;

  if (selectedOption) {
    return (
      <div className="container mx-auto px-4 py-12 min-h-screen">
        <div className="mb-8">
          <button
            onClick={() => setSelectedOption(null)}
            className="text-gray-400 hover:text-gold transition-colors"
          >
            ← Back to Giving Options
          </button>
        </div>
        <DonationWidget
          preselectedAmount={selectedOption.amount}
          preselectedSchedule={selectedOption.schedule}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 min-h-screen">
      {/* Main Heading */}
      <div className="text-center mb-12">
        <h1 className="text-xl md:text-2xl font-bold text-white uppercase tracking-wider">
          GIVING OPTIONS
        </h1>
      </div>

      {/* Giving Options Grid */}
      <div className="max-w-7xl mx-auto pt-[60px]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {givingOptions.map((option) => (
            <div
              key={option.id}
              className="flex flex-col items-center text-center"
            >
              {/* Title */}
              <h2 className="text-lg font-bold text-white mb-6 uppercase tracking-wider">
                {option.title}
              </h2>

              {/* Amount */}
              <div className="text-xl font-bold text-white mb-8">
                {option.amount}
              </div>

              {/* Description (optional) */}
              {option.description && (
                <p className="text-gray-400 text-sm mb-6">{option.description}</p>
              )}

              {/* Button */}
              <Button
                onClick={() =>
                  setSelectedOption({
                    schedule: option.schedule,
                    amount: option.amountInCents,
                  })
                }
                className="w-full bg-[#dd9934] hover:bg-[#121212] text-white font-bold py-4 px-8"
              >
                JOIN THE COVENANT
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Optional tagline or footer text */}
      <div className="text-center mt-20">
        <p className="text-gray-400 text-lg">
          Join us in this journey. Your support today helps shape the programming of tomorrow.
        </p>
      </div>
    </div>
  );
}

export default DonationPage;
